import { AI_V2_LIMITS, AI_V2_PROTOCOL_VERSION, type AiV2Photo, type AiV2Placement, type AiV2SortArea, type AiV2SortRequest } from '../../supabase/functions/_shared/aiV2Protocol';
import { currentAccessToken, getAiSortTransport } from '../backend/aiClient';
import { prepareImageForAi } from '../backend/aiImagePrep';
import { observationBelongsToRow, observationIsUnclassified } from '../domain/aiObservation';
import { CHECKLIST_TEMPLATE_EN_US } from '../domain/checklistTemplates.en-US';
import type { V2Evidence } from '../domain/evidence';
import { newId } from '../domain/ids';
import { isInspectionWorkable, type V2Inspection } from '../domain/inspection';
import { currentLocale } from '../i18n/i18n';
import { areaDisplayName } from '../i18n/labels';
import { accountGuard } from '../persistence/accountScope';
import { getMedia, putRecords } from '../persistence/v2Database';
import { areaSeconds, POOL_AREA_CONCURRENCY, POOL_SORT_BATCH, readPoolAiSpeed, rememberPoolAiSpeed, sortBatchesOf, sortRequestSeconds, watchAppHidden, type PoolAiClock, type PoolAiJobClock } from './poolAiTiming';
import { aiPolicyBlockFor, observationsOfArea, runAreaAnalysisAction, type AiFailureCode, type AiState } from './aiStore';
import { applyServerQuota } from './billingStore';
import { createStore } from './createStore';
import { evidenceStore, type EvidenceState } from './evidenceStore';
import { findingStore, type FindingState } from './findingStore';
import { areasOf, inspectionStore, itemsOf, prepareItemEvidenceSuggestions, type InspectionState } from './inspectionStore';
import { areaScanPhotos, buildPhotoPool, entryIsUnclassified } from './photoPool';
import { sessionStore } from './sessionStore';

// 「AI 分析照片池」 (Owner device test 2026-10-05): 「导入完成没有明显的AI分析全部照片的按钮…这里需要增加AI分析照片池
// 内容的选项。然后按分类直接把照片归类进各个项，并处理好各种分析结果和建议报告语。最终由验屋师查看修改。」
//
//   1 SORT     the pool photos nothing has claimed yet go to the ai-v2 pool sort in small batches: for
//              each photo, the area of the house it shows and the rows of that area it is evidence for.
//              Every batch must answer before anything is filed — a half-sorted pool is never left behind;
//   2 FILE     each photo joins the rows it was placed on — an evidence LINK, nothing is moved or copied,
//              and never a row the inspector already removed it from (CA-7 / §9.2). A photo the AI could
//              not place stays in the pool's 未分类 for the inspector;
//   3 ANALYZE  each area's photos go through the UNCHANGED area scan (runAreaAnalysisAction): the Scope
//              Gate, the rules library, observations on rows with their report-language sentences, the
//              area's unclassified list, scope reminders and limitation prompts — exactly as an area scan
//              the inspector started. The pool run ADDS to what earlier scans of the area found.
//
// The AI never decides here either: no result, no note, no finding, no report sentence is written — the
// inspector reviews every area and decides (CA-8 / CA-11). The run and its outcome are in memory; what
// it filed and found is persisted by the stores that own it.

// The sort's batch size and the areas analyzed at once live with the time estimate (poolAiTiming.ts).
export { POOL_AREA_CONCURRENCY, POOL_SORT_BATCH };
// The sort recognizes the room and the component; it does not read hairline cracks. 1024 px costs well
// under the area scan's 1280 px and is plenty for that.
export const POOL_SORT_IMAGE_EDGE = 1024;
export const POOL_SORT_IMAGE_QUALITY = 0.8;

export type PoolAiPhase = 'sorting' | 'analyzing';

export interface PoolAiLive {
  phase: PoolAiPhase;
  photos: number;
  sorted: number;
  areasTotal: number;
  areasDone: number;
  // The areas being analyzed right now.
  activeAreaIds: string[];
  startedAt: number;
  // What the time estimate is worked out from: the requests' photos and the real time each one took.
  clock: PoolAiClock;
}

export interface PoolAiAreaOutcome {
  areaId: string;
  // Every photo the sort placed in this area.
  photoIds: string[];
  // The ones whose analysis did not run or failed (what a retry sends).
  pendingPhotoIds: string[];
  // null = analyzed.
  failure: AiFailureCode | null;
}

// What the outcome card shows is read from the stores when it renders (src/features/import/PoolAiCard.tsx):
// a photo counts as filed while it is linked to a row or a finding — so the card, the pool and the area
// page always agree, also after the inspector links or deletes a photo.
export interface PoolAiResult {
  finishedAt: number;
  // Every photo this run sent to the sort.
  photoIds: string[];
  areas: PoolAiAreaOutcome[];
  // The run stopped before anything was filed (the sort failed, the allowance ran out …).
  failure: AiFailureCode | null;
  // 2026-10-19 (#4): the photos the sort was not sure about — analyzed in their likely area, left in 未分类,
  // their observations' photos proposed to rows for the inspector to confirm. Absent on older results.
  uncertainPhotoIds?: string[];
}

export interface PoolAiState {
  live: Record<string, PoolAiLive>;
  results: Record<string, PoolAiResult>;
}

export const poolAiStore = createStore<PoolAiState>({ live: {}, results: {} });

// Also called on every account enter / leave (clearAccountStores): this phone's measured speed
// (poolAiTiming.ts) is a device fact and stays.
export function resetPoolAiStoreForTests(): void {
  poolAiStore.set(() => ({ live: {}, results: {} }));
}

function setLive(inspectionId: string, live: PoolAiLive | null): void {
  poolAiStore.set((prev) => {
    const next = { ...prev.live };
    if (live) next[inspectionId] = live;
    else delete next[inspectionId];
    return { ...prev, live: next };
  });
}

function patchLive(inspectionId: string, patch: (live: PoolAiLive) => Partial<PoolAiLive>): void {
  poolAiStore.set((prev) => {
    const current = prev.live[inspectionId];
    return current ? { ...prev, live: { ...prev.live, [inspectionId]: { ...current, ...patch(current) } } } : prev;
  });
}

function patchClock(inspectionId: string, patch: (clock: PoolAiClock) => Partial<PoolAiClock>): void {
  patchLive(inspectionId, (live) => ({ clock: { ...live.clock, ...patch(live.clock) } }));
}

function patchJob(inspectionId: string, areaId: string, patch: Partial<PoolAiJobClock>): void {
  patchClock(inspectionId, (clock) => ({ jobs: clock.jobs.map((j) => (j.areaId === areaId ? { ...j, ...patch } : j)) }));
}

function setResult(inspectionId: string, result: PoolAiResult): void {
  poolAiStore.set((prev) => ({ ...prev, results: { ...prev.results, [inspectionId]: result } }));
}

function union(a: readonly string[], b: readonly string[]): string[] {
  return [...new Set([...a, ...b])];
}

// The outcome card is everything the AI did with this pool, not only the last run: a second run (the
// photos left over, or photos imported later) adds its areas to the first one's. A run that stopped keeps
// the areas already done and says why it stopped.
function mergedResult(previous: PoolAiResult | undefined, fresh: PoolAiResult): PoolAiResult {
  if (!previous) return fresh;
  const areas = previous.areas.map((before) => {
    const now = fresh.areas.find((a) => a.areaId === before.areaId);
    if (!now) return before;
    const pending = union(before.pendingPhotoIds, now.pendingPhotoIds);
    return { areaId: before.areaId, photoIds: union(before.photoIds, now.photoIds), pendingPhotoIds: pending, failure: now.failure ?? (before.pendingPhotoIds.length > 0 ? before.failure : null) };
  });
  for (const now of fresh.areas) if (!areas.some((a) => a.areaId === now.areaId)) areas.push(now);
  return { finishedAt: fresh.finishedAt, photoIds: union(previous.photoIds, fresh.photoIds), areas, failure: fresh.failure, uncertainPhotoIds: union(previous.uncertainPhotoIds ?? [], fresh.uncertainPhotoIds ?? []) };
}

// The run's uncertain photos the inspector has not confirmed yet: still in 未分类, or carrying only the PENDING
// proposal the area scan made for them (「AI 建议 · 待确认」). A confirmed link settles the photo.
export function poolAiUncertainLeft(result: PoolAiResult, inspectionId: string, evidence: EvidenceState, inspection: InspectionState, findings: FindingState): number {
  const uncertain = new Set(result.uncertainPhotoIds ?? []);
  if (uncertain.size === 0) return 0;
  return buildPhotoPool(inspectionId, evidence, inspection, findings).filter((entry) => uncertain.has(entry.evidence.id) && entry.evidence.areaId === null && entry.links.every((l) => l.pending === true)).length;
}

// ---------- selectors ----------

// What 「AI 分析照片池」 works on: the inspection's pool photos nothing has claimed yet — no capture area and
// no link to a row or a finding (the pool's 未分类).
export function poolAiCandidates(inspectionId: string, evidence: EvidenceState = evidenceStore.get(), inspection: InspectionState = inspectionStore.get(), findings: FindingState = findingStore.get()): V2Evidence[] {
  return buildPhotoPool(inspectionId, evidence, inspection, findings)
    .filter(entryIsUnclassified)
    .map((entry) => entry.evidence);
}

export function poolAiLiveOf(state: PoolAiState, inspectionId: string): PoolAiLive | null {
  return state.live[inspectionId] ?? null;
}

export function poolAiResultOf(state: PoolAiState, inspectionId: string): PoolAiResult | null {
  return state.results[inspectionId] ?? null;
}

// The time estimate (before the run and while it runs) is src/state/poolAiTiming.ts.

// What one area's results card line says: the run's photos filed in the area now, the rows that carry
// an AI observation and the observations still waiting in the area's unclassified list.
export function poolAreaFindings(ai: AiState, evidence: EvidenceState, inspection: InspectionState, findings: FindingState, result: PoolAiResult, areaId: string): { photos: number; rows: number; unclassified: number } {
  const ran = new Set(result.photoIds);
  const observations = observationsOfArea(ai, areaId);
  return {
    photos: areaScanPhotos(evidence, inspection, findings, areaId).filter((e) => ran.has(e.id)).length,
    rows: new Set(observations.filter(observationBelongsToRow).map((o) => o.itemId as string)).size,
    unclassified: observations.filter(observationIsUnclassified).length
  };
}

// The run's photos as they stand now: filed (linked to a row or a finding) or still in 未分类 — the
// photos the AI could not place, or placed in an area without matching any of its rows. Deleted photos
// count in neither.
export function poolAiFiling(result: PoolAiResult, inspectionId: string, evidence: EvidenceState, inspection: InspectionState, findings: FindingState): { filed: number; left: number } {
  const ran = new Set(result.photoIds);
  const entries = buildPhotoPool(inspectionId, evidence, inspection, findings).filter((entry) => ran.has(entry.evidence.id));
  const left = entries.filter(entryIsUnclassified).length;
  return { filed: entries.length - left, left };
}

// ---------- the run ----------

export type PoolAiOutcome = { ok: true; result: PoolAiResult } | { ok: false; code: AiFailureCode };

function isStopCode(code: AiFailureCode): boolean {
  return code === 'quotaExhausted' || code === 'notSignedIn' || code === 'billingUnavailable' || code === 'locked' || String(code).startsWith('blocked');
}

function sortAreasOf(inspection: V2Inspection): AiV2SortArea[] {
  const state = inspectionStore.get();
  return areasOf(state, inspection.id)
    .filter((area) => area.enabled)
    .map((area) => ({
      areaId: area.id,
      areaName: areaDisplayName(currentLocale(), area),
      areaNameEn: areaDisplayName('en-US', area),
      areaKind: area.kind,
      floor: area.floor,
      rows: itemsOf(state, area.id).map((item) => {
        const en = item.templateId ? CHECKLIST_TEMPLATE_EN_US[item.templateId] : undefined;
        return { id: item.id, title: item.title, titleEn: en && en.title !== item.title ? en.title : null };
      })
    }));
}

// Photos → wire form at the sort's size. A photo the device cannot read is not sent and stays unplaced.
async function preparePoolPhotos(photoIds: readonly string[]): Promise<AiV2Photo[]> {
  const byId = new Map(evidenceStore.get().evidence.map((e) => [e.id, e] as const));
  const photos: AiV2Photo[] = [];
  for (const id of photoIds) {
    const record = byId.get(id);
    if (!record) continue;
    try {
      const blob = await getMedia(id, 'full');
      const prepared = blob ? await prepareImageForAi(blob, { maxEdge: POOL_SORT_IMAGE_EDGE, quality: POOL_SORT_IMAGE_QUALITY }) : null;
      if (prepared && prepared.base64.length <= AI_V2_LIMITS.maxPhotoBase64Chars) {
        photos.push({ id, mediaType: prepared.mediaType, base64: prepared.base64, note: record.caption, capturedAt: record.createdAt });
      }
    } catch {
      // unreadable — stays unplaced
    }
  }
  return photos;
}

type SortBatchOutcome = { ok: true; placements: AiV2Placement[] } | { ok: false; code: AiFailureCode };

async function sortBatch(inspection: V2Inspection, areas: AiV2SortArea[], photoIds: readonly string[], sameAccount: () => boolean): Promise<SortBatchOutcome> {
  const photos = await preparePoolPhotos(photoIds);
  if (photos.length === 0) return { ok: true, placements: [] };
  const request: AiV2SortRequest = {
    protocolVersion: AI_V2_PROTOCOL_VERSION,
    kind: 'poolSort',
    requestId: newId('aisort'),
    uiLocale: currentLocale(),
    reportLocale: inspection.reportLanguage === 'zh-CN' ? 'zh-CN' : 'en-US',
    propertyType: inspection.property.propertyType,
    yearBuilt: inspection.property.yearBuilt,
    areas,
    photos,
    inspectionId: inspection.id
  };
  const token = await currentAccessToken();
  // The token is the CURRENT session's: one account's photos never travel under another's sign-in.
  if (!token || !sameAccount()) return { ok: false, code: 'notSignedIn' };
  // LAYER 3 — THE CHOKEPOINT of this seam (PP-21, guarded by tests/guards/aiEntryPoints.guard.test.ts):
  // the gate is read again from the store immediately before the photos leave the device; an
  // inspection that cannot be resolved is a real client. A transport that throws is a network failure.
  const subject = inspectionStore.get().inspections.find((i) => i.id === inspection.id) ?? null;
  const sortBlock = subject ? aiPolicyBlockFor(subject) : 'blockedRealClient';
  if (sortBlock) return { ok: false, code: sortBlock };
  const result = await getAiSortTransport()(request, { accessToken: token }).catch((): null => null);
  // Account isolation (see aiStore executeRun): an answer for the account that was left is dropped whole.
  if (!sameAccount()) return { ok: false, code: 'notSignedIn' };
  if (!result) return { ok: false, code: 'network' };
  if (result.kind === 'response' && result.response.quota) applyServerQuota(result.response.quota);
  if (result.kind === 'failed') return { ok: false, code: result.code };
  if (!result.response.ok) return { ok: false, code: result.response.code };
  if (result.response.kind !== 'poolSort') return { ok: false, code: 'malformedResponse' };
  return { ok: true, placements: result.response.placements };
}

// Runs the tasks with at most `limit` in flight.
async function runLimited(tasks: ReadonlyArray<() => Promise<void>>, limit: number): Promise<void> {
  let next = 0;
  const worker = async (): Promise<void> => {
    for (;;) {
      const index = next;
      next += 1;
      if (index >= tasks.length) return;
      await tasks[index]();
    }
  };
  await Promise.all(Array.from({ length: Math.max(1, Math.min(limit, tasks.length)) }, () => worker()));
}

// Each area's photos through the unchanged area scan, at most AI_V2_LIMITS.maxPhotos per run, adding to
// what earlier scans of the area found. Stops launching areas once the allowance, the sign-in or the
// policy says no; the areas it did not reach keep their photos pending for a retry.
async function analyzeAreas(inspectionId: string, jobs: readonly PoolAiAreaOutcome[], sameAccount: () => boolean, appHidden: () => boolean): Promise<PoolAiAreaOutcome[]> {
  const order = new Map(areasOf(inspectionStore.get(), inspectionId).map((a, index) => [a.id, index] as const));
  const done: PoolAiAreaOutcome[] = [];
  let stop: AiFailureCode | null = null;
  // The real time of each area, for the estimate of the areas still to come and for the next run.
  const timed: Array<{ actualSeconds: number; predictedSeconds: number }> = [];
  patchLive(inspectionId, (live) => ({
    phase: 'analyzing',
    areasTotal: jobs.length,
    areasDone: 0,
    activeAreaIds: [],
    clock: { ...live.clock, jobs: jobs.map((job) => ({ areaId: job.areaId, photos: job.pendingPhotoIds.length, startedAt: null, finishedAt: null, ok: false })) }
  }));
  await runLimited(
    jobs.map((job) => async () => {
      if (stop !== null || !sameAccount()) {
        done.push({ ...job, failure: stop ?? 'notSignedIn' });
        const skippedAt = Date.now();
        patchJob(inspectionId, job.areaId, { startedAt: skippedAt, finishedAt: skippedAt, ok: false });
        return;
      }
      const startedAt = Date.now();
      patchJob(inspectionId, job.areaId, { startedAt });
      patchLive(inspectionId, (live) => ({ activeAreaIds: [...live.activeAreaIds, job.areaId] }));
      let pending = [...job.pendingPhotoIds];
      let failure: AiFailureCode | null = null;
      while (pending.length > 0) {
        const batch = pending.slice(0, AI_V2_LIMITS.maxPhotos);
        const outcome = await runAreaAnalysisAction(job.areaId, { photoIds: batch, append: true });
        if (!outcome.ok) {
          failure = outcome.code;
          break;
        }
        pending = pending.slice(batch.length);
      }
      if (failure !== null && isStopCode(failure)) stop = failure;
      done.push({ ...job, pendingPhotoIds: pending, failure });
      const finishedAt = Date.now();
      if (failure === null) timed.push({ actualSeconds: (finishedAt - startedAt) / 1000, predictedSeconds: areaSeconds(job.pendingPhotoIds.length) });
      patchJob(inspectionId, job.areaId, { finishedAt, ok: failure === null });
      patchLive(inspectionId, (live) => ({ areasDone: live.areasDone + 1, activeAreaIds: live.activeAreaIds.filter((id) => id !== job.areaId) }));
    }),
    POOL_AREA_CONCURRENCY
  );
  if (!appHidden()) rememberPoolAiSpeed('area', timed);
  return done.sort((a, b) => (order.get(a.areaId) ?? 0) - (order.get(b.areaId) ?? 0));
}

function newClock(startedAt: number, areasAvailable: number, sortPhotos: number): PoolAiClock {
  return { speed: readPoolAiSpeed(), areasAvailable, sortBatches: sortBatchesOf(sortPhotos), sortSeconds: [], sortSentAt: startedAt, jobs: [] };
}

export async function runPoolAnalysisAction(inspectionId: string, options: { now?: number } = {}): Promise<PoolAiOutcome> {
  const inspection = inspectionStore.get().inspections.find((i) => i.id === inspectionId) ?? null;
  if (!inspection) return { ok: false, code: 'notFound' };
  if (!isInspectionWorkable(inspection)) return { ok: false, code: 'locked' };
  // LAYER 2 (PP-21): refuse before any photo is prepared and before anything leaves the device.
  const policyBlock = aiPolicyBlockFor(inspection);
  if (policyBlock) return { ok: false, code: policyBlock };
  if (poolAiStore.get().live[inspectionId]) return { ok: false, code: 'busy' };
  if (sessionStore.get().phase !== 'signedIn') return { ok: false, code: 'notSignedIn' };
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return { ok: false, code: 'offline' };
  const photoIds = poolAiCandidates(inspectionId).map((e) => e.id);
  if (photoIds.length === 0) return { ok: false, code: 'noPhotos' };
  const areas = sortAreasOf(inspection);
  if (areas.length === 0) return { ok: false, code: 'notFound' };
  const sameAccount = accountGuard();
  const startedAt = Date.now();
  setLive(inspectionId, { phase: 'sorting', photos: photoIds.length, sorted: 0, areasTotal: 0, areasDone: 0, activeAreaIds: [], startedAt, clock: newClock(startedAt, areas.length, photoIds.length) });
  const appHidden = watchAppHidden();
  const previous = poolAiStore.get().results[inspectionId];
  const stopped = (code: AiFailureCode): PoolAiOutcome => {
    // Nothing of this run was filed: its photos stay in the pool, the areas of earlier runs stay listed.
    if (sameAccount()) setResult(inspectionId, mergedResult(previous, { finishedAt: Date.now(), photoIds: [], areas: [], failure: code }));
    return { ok: false, code };
  };
  try {
    // 1 SORT — every batch answers before anything is filed.
    const placements: AiV2Placement[] = [];
    const sortTimed: Array<{ actualSeconds: number; predictedSeconds: number }> = [];
    for (let at = 0; at < photoIds.length; at += POOL_SORT_BATCH) {
      const batch = photoIds.slice(at, at + POOL_SORT_BATCH);
      const sentAt = Date.now();
      patchClock(inspectionId, () => ({ sortSentAt: sentAt }));
      const sorted = await sortBatch(inspection, areas, batch, sameAccount);
      if (!sorted.ok) return stopped(sorted.code);
      placements.push(...sorted.placements.filter((p) => batch.includes(p.photoId)));
      const seconds = (Date.now() - sentAt) / 1000;
      sortTimed.push({ actualSeconds: seconds, predictedSeconds: sortRequestSeconds(batch.length) });
      patchLive(inspectionId, (live) => ({ sorted: Math.min(photoIds.length, at + batch.length), clock: { ...live.clock, sortSeconds: [...live.clock.sortSeconds, seconds] } }));
    }
    if (!appHidden.wasHidden()) rememberPoolAiSpeed('sort', sortTimed);
    if (!sameAccount()) return { ok: false, code: 'notSignedIn' };

    // 2 FILE — the rows the photos were placed on (only rows of the placed area, of this inspection).
    const current = inspectionStore.get().inspections.find((i) => i.id === inspectionId) ?? null;
    if (!current || !isInspectionWorkable(current)) return stopped('locked');
    const itemById = new Map(inspectionStore.get().items.filter((i) => i.inspectionId === inspectionId).map((i) => [i.id, i] as const));
    const areaIds = new Set(areas.map((a) => a.areaId));
    const rowLinks = new Map<string, string[]>();
    const byArea = new Map<string, string[]>();
    const uncertain: string[] = [];
    for (const placement of placements) {
      if (placement.areaId === null || !areaIds.has(placement.areaId)) continue;
      byArea.set(placement.areaId, [...(byArea.get(placement.areaId) ?? []), placement.photoId]);
      // 2026-10-19 (Owner's 8 points, #4, revised the same day): a placement the sort is NOT sure about
      // (confidence low — about 4 in 10 of them were in the wrong area in the 160-photo runs, against 1 in 16 of
      // the others) is still ANALYZED in that area, so nothing is left unread, but the sort's row guess is not
      // proposed: the photo stays in the pool's 未分类, and only what the area scan observes on it reaches a
      // row — as 「AI 建议 · 待确认」 the inspector confirms. (Leaving such photos out of the analysis altogether
      // lost 12 of the Owner's 38 recorded conditions in one run.)
      if (placement.confidence === 'low') {
        uncertain.push(placement.photoId);
        continue;
      }
      for (const itemId of placement.itemIds) {
        if (itemById.get(itemId)?.areaId !== placement.areaId) continue;
        rowLinks.set(itemId, [...(rowLinks.get(itemId) ?? []), placement.photoId]);
      }
    }
    // 2026-10-18: a PROPOSAL on each row (「AI 建议 · 待确认」), never a link — the inspector confirms it on the row.
    const links = prepareItemEvidenceSuggestions(rowLinks, options.now ?? Date.now());
    await putRecords(links.writes);
    links.apply();

    // 3 ANALYZE — area by area, in the inspection's area order.
    const jobs: PoolAiAreaOutcome[] = areasOf(inspectionStore.get(), inspectionId)
      .filter((area) => byArea.has(area.id))
      .map((area) => {
        const ids = byArea.get(area.id) ?? [];
        return { areaId: area.id, photoIds: ids, pendingPhotoIds: ids, failure: null };
      });
    const outcomes = await analyzeAreas(inspectionId, jobs, sameAccount, appHidden.wasHidden);
    if (!sameAccount()) return { ok: false, code: 'notSignedIn' };
    const result = mergedResult(previous, { finishedAt: Date.now(), photoIds, areas: outcomes, failure: null, uncertainPhotoIds: uncertain });
    setResult(inspectionId, result);
    return { ok: true, result };
  } finally {
    appHidden.stop();
    setLive(inspectionId, null);
  }
}

// 重试没分析成的区域: the areas of the last run whose analysis did not finish, with the photos still pending.
export async function retryPoolAreasAction(inspectionId: string): Promise<PoolAiOutcome> {
  const last = poolAiStore.get().results[inspectionId];
  if (!last) return { ok: false, code: 'notFound' };
  const inspection = inspectionStore.get().inspections.find((i) => i.id === inspectionId) ?? null;
  if (!inspection) return { ok: false, code: 'notFound' };
  if (!isInspectionWorkable(inspection)) return { ok: false, code: 'locked' };
  const policyBlock = aiPolicyBlockFor(inspection);
  if (policyBlock) return { ok: false, code: policyBlock };
  if (poolAiStore.get().live[inspectionId]) return { ok: false, code: 'busy' };
  const jobs = last.areas.filter((a) => a.failure !== null && a.pendingPhotoIds.length > 0);
  if (jobs.length === 0) return { ok: true, result: last };
  const sameAccount = accountGuard();
  const startedAt = Date.now();
  setLive(inspectionId, { phase: 'analyzing', photos: last.photoIds.length, sorted: last.photoIds.length, areasTotal: jobs.length, areasDone: 0, activeAreaIds: [], startedAt, clock: newClock(startedAt, jobs.length, 0) });
  const appHidden = watchAppHidden();
  try {
    const outcomes = await analyzeAreas(inspectionId, jobs, sameAccount, appHidden.wasHidden);
    if (!sameAccount()) return { ok: false, code: 'notSignedIn' };
    const byArea = new Map(outcomes.map((o) => [o.areaId, o] as const));
    const result: PoolAiResult = { ...last, finishedAt: Date.now(), areas: last.areas.map((a) => byArea.get(a.areaId) ?? a) };
    setResult(inspectionId, result);
    return { ok: true, result };
  } finally {
    appHidden.stop();
    setLive(inspectionId, null);
  }
}
